import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PauseCircle, PlayCircle, ArrowsClockwise, CheckCircle, Trash } from "@phosphor-icons/react";
import { SendBackDialog, ProRevokeDialog } from "@/components/PracticeExtraDialogs";

const PAID = ["paid", "via_plafond", "authorized"];

// Azioni spostate dalla card Kanban: Pausa/Riprendi, Rimanda, Sblocca e avanza, Saldo ricevuto, Revoca incarico PRO.
export const PracticeExtraActions = ({ p, user, isAdmin, isAssignee, myOperatorState, onDone }) => {
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [proRevokeOpen, setProRevokeOpen] = useState(false);
  const active = !["completed", "cancelled"].includes(p.status);
  const isOperator = isAssignee && !isAdmin;
  const steps = [...(p.workflow_steps_snapshot || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const canSendBack = isOperator && myOperatorState === "in_lavorazione"
    && steps.findIndex((s) => s.id === p.current_step_id) > 0;
  const awaitingClientPayment = isAdmin && active && p.status !== "awaiting_balance"
    && p.is_shared_with_client && !p.payment_override && !PAID.includes(p.payment_status);
  const curState = (p.operator_states || {})[p.current_assigned_to];
  const canProRevoke = user.role === "professional" && p.professional_id === user.id
    && active && curState === "assegnazione";

  const call = async (fn, ok, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await fn(); toast.success(ok); onDone && onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const standby = (v) => call(
    () => api.patch(`/practices/${p._id}/standby`, { stand_by: v }),
    v ? "Messa in stand by" : "Ripresa in lavorazione",
  );

  return (
    <>
      {active && isOperator && myOperatorState === "in_lavorazione" && (
        <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={() => standby(true)} data-testid="operator-standby-btn">
          <PauseCircle size={14} className="mr-2" weight="bold" /> Metti in pausa
        </Button>
      )}
      {active && isOperator && myOperatorState === "in_stand_by" && (
        <Button className="ht-gradient border-0" onClick={() => standby(false)} data-testid="operator-resume-btn">
          <PlayCircle size={14} className="mr-2" /> Riprendi
        </Button>
      )}
      {active && canSendBack && (
        <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
          onClick={() => setSendBackOpen(true)} data-testid="operator-send-back-btn">
          <ArrowsClockwise size={14} className="mr-2" weight="bold" /> Rimanda indietro
        </Button>
      )}
      {awaitingClientPayment && (
        <Button className="bg-orange-600 hover:bg-orange-700 text-white border-0"
          onClick={() => call(() => api.post(`/practices/${p._id}/force-release`),
            "Pratica sbloccata: passa all'operatore assegnato",
            "Vuoi sbloccare questa pratica e farla avanzare senza attendere il pagamento del cliente?")}
          data-testid="admin-force-release-btn">
          <PlayCircle size={14} className="mr-2" /> Sblocca e avanza
        </Button>
      )}
      {isAdmin && p.status === "awaiting_balance" && (
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
          onClick={() => call(() => api.post(`/practices/${p._id}/mark-payment-received`),
            "Saldo confermato: consegna sbloccata",
            "Confermi la ricezione del saldo? La pratica verrà completata, i documenti sbloccati e il PRO/cliente verranno notificati.")}
          data-testid="admin-balance-received-btn">
          <CheckCircle size={14} className="mr-2" /> Saldo ricevuto
        </Button>
      )}
      {canProRevoke && (
        <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
          onClick={() => setProRevokeOpen(true)} data-testid="pro-revoke-btn">
          <Trash size={14} className="mr-2" /> Revoca incarico
        </Button>
      )}
      <SendBackDialog open={sendBackOpen} onClose={() => setSendBackOpen(false)} practiceId={p._id} onDone={onDone} />
      <ProRevokeDialog
        open={proRevokeOpen}
        onClose={() => setProRevokeOpen(false)}
        practiceId={p._id}
        serviceName={p.service_name}
        paidByClient={p.is_shared_with_client && p.payment_status === "paid"}
        onDone={onDone}
      />
    </>
  );
};
